import { createQueryParams } from '@/utils/query-params';
import { api } from './index';
import type { ShiftCalendarEvent } from './shifts-calendar-events';

const BASE_PATH = '/api/internal/patient_schedule_blocks';

// Flag carried by every calendar shift that sits inside a block.
export type ShiftScheduleBlockFlag = { id: number; description: string } | null;

// Both calendar payloads ship the blocks of their range with company-TZ
// wall-clock fields (startDate/startTime/endDate/endTime); the calendars
// position blocks with those strings, never by parsing startsAt/endsAt.
export type PatientScheduleBlock = {
  id: number;
  patientId: number;
  patientFullName: string;
  patientDni: string | null;
  startsAt: string;
  endsAt: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  careers: string[];
  humanCareers: string;
  description: string;
  createdByName: string | null;
  createdAt: string;
};

export interface PatientScheduleBlockForm {
  patientId: number;
  // Offset-less 'YYYY-MM-DDTHH:MM', parsed by the API in the company time zone.
  startsAt: string;
  endsAt: string;
  description: string;
  careers: string[];
}

// Prefill for the create dialog (from a calendar cell or the toolbar).
export interface ScheduleBlockPrefill {
  patientId?: number;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

export interface ScheduleBlockConflictsParams {
  patientId: number;
  startsAt: string;
  endsAt: string;
  careers: string[];
  exceptId?: number;
}

export type ScheduleBlockConflicts = {
  shifts: ShiftCalendarEvent[];
  totalCount: number;
};

export const patientScheduleBlocksApi = {
  create(form: PatientScheduleBlockForm): Promise<PatientScheduleBlock> {
    return api({
      method: 'post',
      url: BASE_PATH,
      data: { patientScheduleBlock: form },
    }).then(response => response.data.patientScheduleBlock);
  },
  update(id: number, form: Omit<PatientScheduleBlockForm, 'patientId'>): Promise<PatientScheduleBlock> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${id}`,
      data: { patientScheduleBlock: form },
    }).then(response => response.data.patientScheduleBlock);
  },
  destroy(id: number): Promise<void> {
    return api({
      method: 'delete',
      url: `${BASE_PATH}/${id}`,
    }).then(() => undefined);
  },
  // Shifts a candidate block would cover (informational: saving never touches them).
  conflicts(params: ScheduleBlockConflictsParams): Promise<ScheduleBlockConflicts> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/conflicts?${createQueryParams(params)}`,
    }).then(response => ({
      shifts: response.data.shifts,
      totalCount: response.data.meta?.totalCount ?? response.data.shifts.length,
    }));
  },
};
